import type * as React from 'react';
import { useRef } from 'react';
import { Camera, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

/* ── Deterministic avatar colour from player name ─────────── */
export const AVATAR_COLORS = [
  ['#d1fae5', '#059669'],
  ['#dbeafe', '#2563eb'],
  ['#fce7f3', '#db2777'],
  ['#ede9fe', '#7c3aed'],
  ['#ffedd5', '#ea580c'],
  ['#fef9c3', '#ca8a04'],
  ['#e0f2fe', '#0284c7'],
  ['#f1f5f9', '#475569'],
];

export function avatarColors(name: string): [string, string] {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) | 0;
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

interface PlayerAvatarProps {
  name: string;
  avatarUrl?: string | null;
  size?: number;
  onClick?: () => void;
  editable?: boolean;
  onUpload?: (file: File) => void;
  uploading?: boolean;
  shape?: 'circle' | 'square';
  className?: string;
  style?: React.CSSProperties;
}

export function PlayerAvatar({
  name,
  avatarUrl,
  size = 40,
  onClick,
  editable = false,
  onUpload,
  uploading = false,
  shape = 'circle',
  className,
  style,
}: PlayerAvatarProps) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [bg, fg] = avatarColors(name);

  const handleClick = (e: React.MouseEvent) => {
    if (editable && !uploading) {
      e.stopPropagation();
      fileRef.current?.click();
    } else if (onClick) {
      e.stopPropagation();
      onClick();
    }
  };

  return (
    <div 
      className={cn("relative shrink-0", className)} 
      style={{
        width: size,
        height: size,
        ...style
      }}
    >
      <button
        type="button"
        className={cn(
          'w-full h-full overflow-hidden flex items-center justify-center transition-all',
          shape === 'circle' ? 'rounded-full' : 'rounded-2xl',
          'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1',
          editable && !uploading && 'group cursor-pointer',
          !editable && onClick && 'cursor-pointer',
          !onClick && !editable && 'cursor-default'
        )}
        style={{ background: bg }}
        onClick={handleClick}
        title={editable ? 'Tap to change photo' : name}
      >
        {uploading ? (
          <Loader2 className="animate-spin" style={{ width: size * 0.4, height: size * 0.4, color: fg }} />
        ) : avatarUrl ? (
          <img src={avatarUrl} alt={name} className="w-full h-full object-cover" />
        ) : (
          <span className="font-bold select-none" style={{ color: fg, fontSize: size * 0.4 }}>
            {name.charAt(0).toUpperCase()}
          </span>
        )}

        {/* Hover overlay for editable state */}
        {editable && !uploading && (
          <div className={cn(
            "absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center",
            shape === 'circle' ? 'rounded-full' : 'rounded-2xl'
          )}>
            <Camera className="text-white" style={{ width: size * 0.35, height: size * 0.35 }} />
          </div>
        )}
      </button>

      {editable && (
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) onUpload?.(file);
            e.target.value = '';
          }}
        />
      )}
    </div>
  );
}
